import React, { useState, useEffect, useMemo } from "react";
import { ExternalLink, Clock, Plus, Copy, Trash2, User } from "lucide-react";
import { INK, INK_2, INK_3, LINE, BURGUNDY, BAD_INK, BG } from "../../Bureau/bureauTokens";
import { httpClient } from "../../../HttpClient";
import { useClients } from "../../Portefeuille/useClients";

const DUREES_PROPOSABLES = [15, 30, 45, 60, 90];
const CANAUX_PROPOSABLES = ["Cabinet", "Visio", "Téléphone"];
const DOW_SHORT = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const fieldLabel = "text-[11px] font-medium";
const fieldInput = "h-9 px-3 text-xs outline-none";
const fieldStyle = { border: `1px solid ${LINE}`, borderRadius: 8, color: INK, background: "#fff" };

function chipStyle(on) {
  return {
    borderRadius: 999,
    border: `1px solid ${on ? BURGUNDY : LINE}`,
    background: on ? BURGUNDY : "#fff",
    color: on ? "#fff" : INK_2,
  };
}

// Onglet "Page de réservation" — réglages (activer/titre/message/durées/
// canaux/jours ouverts/heures/battement/délai/fenêtre/confirmation/motif,
// `params`/`onUpdate` partagés avec l'onglet Jours fériés) + générateur de
// lien par client existant (voir plan — pas de prospects anonymes, un lien
// par relation collaborateur/client, régénérer remplace l'ancien).
function ReservationPageSettingsPanel({ params, onUpdate }) {
  const { data: clients } = useClients();
  const [links, setLinks] = useState([]);
  const [clientQ, setClientQ] = useState("");
  const [showList, setShowList] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const fetchLinks = () =>
    httpClient
      .get("/api/reservation-links")
      .then(({ data }) => setLinks(data.data || []))
      .catch((err) => console.error("Erreur lors de la récupération des liens de réservation :", err));

  useEffect(() => {
    fetchLinks();
  }, []);

  const matches = useMemo(() => {
    const q = clientQ.trim().toLowerCase();
    const list = clients || [];
    return (q ? list.filter((c) => c.nom?.toLowerCase().includes(q)) : list).slice(0, 8);
  }, [clientQ, clients]);

  const generateLink = async () => {
    if (!selectedClient) return;
    setGenerating(true);
    setError(null);
    try {
      await httpClient.post("/api/reservation-links", { clientId: selectedClient.id });
      setSelectedClient(null);
      setClientQ("");
      await fetchLinks();
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de la génération du lien.");
    } finally {
      setGenerating(false);
    }
  };

  const revokeLink = async (id) => {
    await httpClient.delete(`/api/reservation-links/${id}`);
    await fetchLinks();
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/rdv/${token}`);
  };

  const toggleIn = (arr, val) => (arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  return (
    <>
      {error && (
        <p className="text-xs mb-2" style={{ color: BAD_INK }}>
          {error}
        </p>
      )}

      <div className="mb-3" style={{ background: "#FFF6F8", border: "1px solid #E8C8D2", borderRadius: 10, padding: "12px 14px" }}>
        <div className="flex items-center gap-1.5 text-xs font-semibold mb-2" style={{ color: BURGUNDY }}>
          <ExternalLink size={13} /> Page de réservation
        </div>
        <label className="flex items-center gap-2 mb-3 text-xs cursor-pointer" style={{ color: INK_2 }}>
          <input type="checkbox" checked={params.activee} onChange={(e) => onUpdate({ activee: e.target.checked })} />
          <span>
            <b>Activer la page de réservation en ligne</b>
          </span>
        </label>
        <label className={`block ${fieldLabel} mb-1`} style={{ color: INK_2 }}>
          Titre affiché
        </label>
        <input value={params.titre} onChange={(e) => onUpdate({ titre: e.target.value })} className={`w-full ${fieldInput} mb-2.5`} style={fieldStyle} />
        <label className={`block ${fieldLabel} mb-1`} style={{ color: INK_2 }}>
          Message d'accueil
        </label>
        <textarea
          value={params.messageAccueil}
          onChange={(e) => onUpdate({ messageAccueil: e.target.value })}
          rows={2}
          className="w-full px-3 py-2 text-xs outline-none resize-y"
          style={fieldStyle}
        />
      </div>

      <div className="mb-3" style={{ background: "#FAFAF8", border: `1px solid ${LINE}`, borderRadius: 10, padding: "12px 14px" }}>
        <div className="flex items-center gap-1.5 text-xs font-semibold mb-2" style={{ color: BURGUNDY }}>
          <Clock size={13} /> Créneaux proposés
        </div>
        <span className={fieldLabel} style={{ color: INK_2 }}>
          Durées de RDV proposées
        </span>
        <div className="flex flex-wrap gap-1.5 mt-1 mb-2.5">
          {DUREES_PROPOSABLES.map((d) => (
            <button key={d} onClick={() => onUpdate({ durees: toggleIn(params.durees, d) })} className="text-[11px] font-semibold px-2.5 py-1" style={chipStyle(params.durees.includes(d))}>
              {d} min
            </button>
          ))}
        </div>
        <span className={fieldLabel} style={{ color: INK_2 }}>
          Durée générée sur la page (une seule à la fois)
        </span>
        <select
          value={params.dureeParDefaut}
          onChange={(e) => onUpdate({ dureeParDefaut: Number(e.target.value) })}
          className={`${fieldInput} mt-1 mb-2.5 appearance-none cursor-pointer`}
          style={fieldStyle}
        >
          {(params.durees.length ? params.durees : DUREES_PROPOSABLES).map((d) => (
            <option key={d} value={d}>
              {d} min
            </option>
          ))}
        </select>
        <span className={fieldLabel} style={{ color: INK_2 }}>
          Canaux proposés
        </span>
        <div className="flex flex-wrap gap-1.5 mt-1 mb-2.5">
          {CANAUX_PROPOSABLES.map((c) => (
            <button key={c} onClick={() => onUpdate({ canaux: toggleIn(params.canaux, c) })} className="text-[11px] font-semibold px-2.5 py-1" style={chipStyle(params.canaux.includes(c))}>
              {c}
            </button>
          ))}
        </div>
        <span className={fieldLabel} style={{ color: INK_2 }}>
          Jours ouverts à la réservation
        </span>
        <div className="flex flex-wrap gap-1.5 mt-1 mb-2.5">
          {DOW_SHORT.map((l, i) => (
            <button key={i} onClick={() => onUpdate({ joursOuverts: toggleIn(params.joursOuverts, i) })} className="text-[11px] font-semibold px-2.5 py-1" style={chipStyle(params.joursOuverts.includes(i))}>
              {l}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1">
            <span className={fieldLabel} style={{ color: INK_2 }}>
              Ouverture
            </span>
            <input type="time" value={params.heureDebut} onChange={(e) => onUpdate({ heureDebut: e.target.value })} className={fieldInput} style={fieldStyle} />
          </label>
          <label className="flex flex-col gap-1">
            <span className={fieldLabel} style={{ color: INK_2 }}>
              Fermeture
            </span>
            <input type="time" value={params.heureFin} onChange={(e) => onUpdate({ heureFin: e.target.value })} className={fieldInput} style={fieldStyle} />
          </label>
        </div>
      </div>

      <div className="mb-3" style={{ background: "#FAFAF8", border: `1px solid ${LINE}`, borderRadius: 10, padding: "12px 14px" }}>
        <div className="flex items-center gap-1.5 text-xs font-semibold mb-2" style={{ color: BURGUNDY }}>
          <Clock size={13} /> Règles de réservation
        </div>
        <div className="grid grid-cols-2 gap-3 mb-2.5">
          <label className="flex flex-col gap-1">
            <span className={fieldLabel} style={{ color: INK_2 }}>
              Battement entre RDV (min)
            </span>
            <select value={params.battement} onChange={(e) => onUpdate({ battement: Number(e.target.value) })} className={`${fieldInput} appearance-none cursor-pointer`} style={fieldStyle}>
              {[0, 5, 10, 15, 30].map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className={fieldLabel} style={{ color: INK_2 }}>
              Délai minimum (heures)
            </span>
            <select value={params.delaiMinimumH} onChange={(e) => onUpdate({ delaiMinimumH: Number(e.target.value) })} className={`${fieldInput} appearance-none cursor-pointer`} style={fieldStyle}>
              {[0, 2, 12, 24, 48, 72].map((v) => (
                <option key={v} value={v}>
                  {v} h
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="flex flex-col gap-1 mb-2.5">
          <span className={fieldLabel} style={{ color: INK_2 }}>
            Fenêtre de réservation (jours)
          </span>
          <select value={params.fenetreJours} onChange={(e) => onUpdate({ fenetreJours: Number(e.target.value) })} className={`${fieldInput} appearance-none cursor-pointer`} style={{ ...fieldStyle, width: 140 }}>
            {[7, 14, 30, 60, 90].map((v) => (
              <option key={v} value={v}>
                {v} j
              </option>
            ))}
          </select>
        </label>
        <span className={fieldLabel} style={{ color: INK_2 }}>
          Confirmation
        </span>
        <div className="flex mt-1 mb-2.5" style={{ border: `1px solid ${LINE}`, borderRadius: 9, overflow: "hidden", width: "fit-content" }}>
          <button
            onClick={() => onUpdate({ confirmationAuto: true })}
            className="text-[11px] font-semibold px-3 py-1.5"
            style={{ background: params.confirmationAuto ? BURGUNDY : "#fff", color: params.confirmationAuto ? "#fff" : INK_2 }}
          >
            Automatique
          </button>
          <button
            onClick={() => onUpdate({ confirmationAuto: false })}
            className="text-[11px] font-semibold px-3 py-1.5"
            style={{ background: !params.confirmationAuto ? BURGUNDY : "#fff", color: !params.confirmationAuto ? "#fff" : INK_2, borderLeft: `1px solid ${LINE}` }}
          >
            Validation manuelle
          </button>
        </div>
        {!params.confirmationAuto && (
          <p className="text-[11px] mb-2.5" style={{ color: INK_3 }}>
            Réglage informatif pour l'instant : le rendez-vous est créé immédiatement dans les deux cas (pas de statut "à confirmer" sur les événements aujourd'hui).
          </p>
        )}
        <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: INK_2 }}>
          <input type="checkbox" checked={params.motifObligatoire} onChange={(e) => onUpdate({ motifObligatoire: e.target.checked })} />
          <span>
            Motif du RDV <b>obligatoire</b>
          </span>
        </label>
      </div>

      <div style={{ background: "#FAFAF8", border: `1px solid ${LINE}`, borderRadius: 10, padding: "12px 14px" }}>
        <div className="flex items-center gap-1.5 text-xs font-semibold mb-2" style={{ color: BURGUNDY }}>
          <Plus size={13} /> Générer un lien pour un client
        </div>
        <div className="relative mb-2.5">
          <input
            value={selectedClient?.nom || clientQ}
            onChange={(e) => {
              setSelectedClient(null);
              setClientQ(e.target.value);
              setShowList(true);
            }}
            onFocus={() => setShowList(true)}
            onBlur={() => setTimeout(() => setShowList(false), 150)}
            placeholder="Rechercher un client…"
            className={`w-full ${fieldInput}`}
            style={fieldStyle}
          />
          {showList && !selectedClient && matches.length > 0 && (
            <div
              className="absolute left-0 right-0 mt-1 overflow-y-auto z-10"
              style={{ top: "100%", background: "#fff", border: `1px solid ${LINE}`, borderRadius: 8, boxShadow: "0 10px 28px rgba(0,0,0,.10)", maxHeight: 180 }}
            >
              {matches.map((c) => (
                <button
                  key={c.id}
                  onMouseDown={() => {
                    setSelectedClient({ id: c.id, nom: c.nom });
                    setClientQ("");
                    setShowList(false);
                  }}
                  className="w-full flex items-center gap-2 text-left text-xs px-3 py-2"
                  style={{ color: INK }}
                >
                  <User size={12} style={{ color: INK_3 }} /> {c.nom}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={generateLink}
          disabled={!selectedClient || generating}
          className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white disabled:opacity-50"
          style={{ background: BURGUNDY, borderRadius: 10 }}
        >
          <Plus size={13} /> {generating ? "Génération…" : "Générer le lien"}
        </button>

        {links.length > 0 && (
          <div className="flex flex-col gap-1.5 mt-3">
            {links.map((l) => (
              <div key={l.id} className="flex items-center gap-2 px-2.5 py-2" style={{ border: `1px solid ${LINE}`, borderRadius: 8, background: "#fff", opacity: l.actif ? 1 : 0.5 }}>
                <span className="text-xs font-semibold flex-1 truncate" style={{ color: INK }}>
                  {l.client?.nom}
                </span>
                {l.actif ? (
                  <>
                    <button onClick={() => copyLink(l.token)} title="Copier le lien" className="flex items-center gap-1 text-[11px] px-2 py-1" style={{ borderRadius: 6, color: INK_2, background: BG }}>
                      <Copy size={12} /> Copier
                    </button>
                    <button onClick={() => revokeLink(l.id)} title="Révoquer" className="flex items-center justify-center" style={{ width: 26, height: 26, borderRadius: 6, color: BAD_INK }}>
                      <Trash2 size={13} />
                    </button>
                  </>
                ) : (
                  <span className="text-[10.5px]" style={{ color: INK_3 }}>
                    révoqué
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default ReservationPageSettingsPanel;
